import React from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useCapabilities } from "../../lib/useCapabilities";
import { canSeeWorkspace, canSeeGlobalAudit, canSeeReviewQueue } from "../../lib/ecosystemApi";

export default function EcosystemLayout() {
  const { user, loading } = useAuth();
  const { caps } = useCapabilities();

  if (loading) return <div className="p-8 text-sm text-slate-500">Loading workspace…</div>;
  if (!user) return <Navigate to="/login?next=/ecosystem" replace />;
  if (!canSeeWorkspace(caps)) {
    // Non-operator patients/survivors/etc. — never leak workspace chrome.
    return (
      <div data-testid="ecosystem-forbidden" className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <div className="text-5xl mb-3">🔒</div>
          <h1 className="text-xl font-semibold text-slate-900">You do not have access to this workspace.</h1>
          <p className="text-slate-500 text-sm mt-2">Ecosystem management is restricted to authorized operators.</p>
        </div>
      </div>
    );
  }

  // BTC3 Phase 3 — reserved role detection via capability projection:
  // no ecosystem read/write/approve bits granted → reserved shell.
  const hasAnyEcosystemBit =
    caps.ecosystem_entity_create ||
    caps.ecosystem_entity_approve_standard ||
    caps.ecosystem_entity_approve_enhanced ||
    caps.ecosystem_relationship_approve ||
    caps.ecosystem_relationship_revoke ||
    caps.ecosystem_grounding_authorize ||
    caps.ecosystem_read_only;
  const reserved = !hasAnyEcosystemBit;

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      <header className="bg-white border-b" data-testid="ecosystem-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-semibold text-slate-900">Ecosystem Workspace</span>
            <span className="text-xs text-slate-500">/ operator ({user.role})</span>
          </div>
          <div className="text-sm text-slate-600 truncate min-w-0" data-testid="ecosystem-current-user">{user.email}</div>
        </div>
        {!reserved && (
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-4 text-sm overflow-x-auto" data-testid="ecosystem-nav">
            <NavItem to="/ecosystem" end testid="ecosystem-nav-overview">Overview</NavItem>
            <NavItem to="/ecosystem/entities" testid="ecosystem-nav-entities">Entities</NavItem>
            {canSeeReviewQueue(caps) && (
              <NavItem to="/ecosystem/review" testid="ecosystem-nav-review">Review Queue</NavItem>
            )}
            <NavItem to="/ecosystem/relationships" testid="ecosystem-nav-relationships">Relationships</NavItem>
            {canSeeGlobalAudit(caps) && (
              <NavItem to="/ecosystem/audit" testid="ecosystem-nav-audit">Audit Log</NavItem>
            )}
          </nav>
        )}
      </header>

      <main className="max-w-7xl mx-auto p-4 sm:p-6">
        {reserved ? (
          <div className="p-6 bg-white rounded border text-sm text-slate-600" data-testid="ecosystem-reserved-role">
            Your role (<span className="font-medium">{user.role}</span>) has no ecosystem management actions yet.
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}

function NavItem({ to, end, testid, children }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `py-2 border-b-2 -mb-px ${isActive ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`
      }
    >
      {children}
    </NavLink>
  );
}
